import React, { useEffect, useRef } from 'react';
import { Grid } from '@naf/grid';
import { Text, TextVariant } from '@naf/text';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Transition } from 'react-transition-group';
import { UserInteraction, UserInteractionVariant } from '@naf/user-interaction';
import { useCloudinary } from '@hooks/Cloudinary/useCloudinary';
import { Layout } from '@components/layout/Layout';
import { useSiteStructure } from '@hooks/SiteStructure/useSiteStructure';
import useSelector from '@redux/typedHooks';
import { actions } from '@redux/modules/centers';
import { CenterProductsType } from '@appTypes/centerProductsType';
import { CentersPageData } from '@appTypes/centerData';
import { useSessionStorage } from '@hooks/useSessionStorage';
import { AnimationWrapper } from '@components/skeleton-loaders/AnimationWrapper';
import { ServiceProductLoader } from '@components/skeleton-loaders/ServiceProductLoader';
import { useResponsiveImage } from '@hooks/useResponsiveImage';
import { getPlaceholderList } from '@utils/getPlaceholderList';
import ErrorBoundary from '@components/error-component/ErrorBoundary';
import { useWindowLocation } from '@hooks/Window/useWindowLocation';
import { InternalSearchCardType } from '@components/layout/InternalSearchMetaData';
import { NafCenterAppType } from '@appTypes/CategoryAppType';
import { NotFound } from '@loadable-elements/NotFound';
import { SectionBlocks } from '@components/SectionBlocks';
import { AdvancedImage } from '@cloudinary/react';
import { DEFAULT_IMG_URL } from '@lib/constants';
import CenterSelect from './CenterSelect/CenterSelect';
import { ProductCard } from './ProductCard/ProductCard';
import S from './styles';

interface NafCenterServicesProps {
  slug: string;
  data: CentersPageData;
  appData?: NafCenterAppType;
}

export const NafCenterServices = ({ slug, data, appData }: NafCenterServicesProps) => {
  const dispatch = useDispatch();
  const [centerId, setCenterId] = useSessionStorage('centerId', undefined);
  const mappedCategory = useSiteStructure(slug);
  const { getProcessedImage } = useCloudinary();
  const windowPath = useWindowLocation();

  const { properties } = appData;
  const { centerList, centerProducts } = useSelector((state) => state.centers);
  const placeholderGridRef = useRef<HTMLDivElement | null>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);
  const selectedCenter = centerId ? centerList?.data?.find((element) => element.centerId === centerId) : undefined;
  const hasNonAvailableProducts = centerProducts.data?.some((product) => !product.isAvailable);

  const categoryImage = getProcessedImage(mappedCategory?.data?.image?.publicId, {
    width: 1200,
    height: 630,
  })?.toURL();

  // Get available centers from API
  useEffect(() => {
    if (!centerList.data && !centerList.isUpdating && !centerList.error) {
      dispatch(actions.getCenters.request(undefined, undefined));
    }
  }, [centerList, dispatch]);

  // Get available center products from API
  useEffect(() => {
    dispatch(actions.getCenterProducts.request(centerId as number));
  }, [centerId, dispatch]);

  const mainImage = useResponsiveImage({
    imageId: data.image?.publicId,
    initialDimensions: { width: 400, height: 448 },
  });

  if (!data) return <NotFound />;

  return (
    <Layout
      title={mappedCategory?.data?.seoConfig?.title || 'NAF Senter'}
      description={
        mappedCategory?.data?.ingress || mappedCategory?.data?.seoConfig?.introduction || 'NAF Senter tjenester'
      }
      imgUrl={categoryImage || DEFAULT_IMG_URL}
      url={windowPath}
      gtmArgs={{
        page_type: 'NAF Senter',
        pageCategory: 'NAF Senter',
        contentId: '',
      }}
      seoTitle={mappedCategory?.data?.seoConfig?.title || 'NAF Senter'}
      seoDescription={mappedCategory?.data?.seoConfig?.introduction || 'NAF Senter tjenester'}
      internalSearchMetaData={{ cardType: InternalSearchCardType.Simple }}
    >
      <S.StyledSectionHeader
        title={mappedCategory?.data?.name}
        imageOrVideo={
          mainImage && <AdvancedImage cldImg={mainImage} alt={data.image?.altText || 'NAF Senter illustrasjon'} />
        }
        ingress={data.ingress}
      />
      <S.StyledMainContent>
        <ErrorBoundary>
          <Grid>
            <CenterSelect
              centerId={centerId}
              handleSelect={(option) => setCenterId(option.value ? parseInt(option.value, 10) : false)}
              selectedCenter={selectedCenter}
              slug={slug}
            />
            <S.TransitionGrid>
              {(!centerProducts.data || centerProducts.isUpdating) && !centerProducts.error ? (
                <Transition
                  nodeRef={placeholderGridRef}
                  in={!centerProducts.data || centerProducts.isUpdating}
                  timeout={{ enter: 250, exit: 0 }}
                  unmountOnExit
                  mountOnEnter
                >
                  {(state) => (
                    <AnimationWrapper ref={placeholderGridRef} $state={state}>
                      <S.MainRow>
                        {getPlaceholderList(12).map((i) => (
                          <S.ShortCutCol s="12" m="6" l="4" xl="4" key={i}>
                            <ServiceProductLoader key={i} />
                          </S.ShortCutCol>
                        ))}
                      </S.MainRow>
                    </AnimationWrapper>
                  )}
                </Transition>
              ) : (
                <Transition
                  nodeRef={gridRef}
                  key="data"
                  in={centerProducts.data && !centerProducts.isUpdating}
                  timeout={250}
                  unmountOnExit
                  mountOnEnter
                >
                  {(state) => (
                    <AnimationWrapper ref={gridRef} $state={state}>
                      <S.MainRow>
                        {centerProducts.data?.map((product) => (
                          <ProductCard
                            isAvailable
                            key={`${product.title}-${product.link.data.slug}`}
                            product={product}
                            path={slug}
                            alternateBackgroundColor={product.useAlternateBackgroundColor}
                          />
                        ))}
                      </S.MainRow>
                    </AnimationWrapper>
                  )}
                </Transition>
              )}
            </S.TransitionGrid>
            {centerProducts.error && (
              <UserInteraction
                maxWidth={300}
                title="Oops, noe gikk galt!"
                variant={UserInteractionVariant.Error}
                button={{
                  onClick: () => {
                    dispatch(actions.getCenterProducts.request(centerId as number));
                  },
                  children: 'Prøv igjen',
                  type: 'button',
                }}
              />
            )}
            <S.LinkCol s="12" m="12" l="12" xl="12">
              <Text>Ønsker du å endre en allerede bestilt time kan du gjøre dette inne på </Text>
              <Link to="/mitt-naf">Mitt NAF</Link>
              <Text>.</Text>
            </S.LinkCol>
            {centerId && centerProducts.data && !centerProducts.isUpdating && hasNonAvailableProducts && (
              <S.MainRow>
                <S.NotAvailableCol s="12" m="12" l="12" xl="12">
                  <Text variant={TextVariant.Header2}>Tilbys ikke på {selectedCenter?.title || 'dette senteret'}</Text>
                  <Text>
                    Dette sentret tilbyr ikke følgende tjenester. Prøv et annet NAF-senter i nærheten for disse
                    tjenstene.
                  </Text>
                </S.NotAvailableCol>
                {centerProducts.data.map((product: CenterProductsType) => (
                  <ProductCard
                    isAvailable={false}
                    key={`${product.title}-${product.link.data.slug}`}
                    product={product}
                    path={slug}
                    alternateBackgroundColor={product.useAlternateBackgroundColor}
                  />
                ))}
              </S.MainRow>
            )}
            {properties?.sections?.map((section) => (
              <S.SectionCol s="12" m="12" l="12" xl="12" key={section.key}>
                <SectionBlocks content={[section]} />
              </S.SectionCol>
            ))}
          </Grid>
        </ErrorBoundary>
      </S.StyledMainContent>
    </Layout>
  );
};
